import { useRouteMatch } from 'react-router';
import { number, object, string } from 'yup';

import { useAiDisplayImageQuery } from 'src/shared/graphql/generated';
import { parseParams } from 'src/shared/hooks/useParsedParams';
import { union } from 'src/shared/validationSchemas';

import { fullscreenImageRoutes } from '../../constants';
import { useIsFullscreenImageOpen } from '../../hooks';
import type { AiOrSampleDisplayImage } from '../../types';
import { getImageId } from '../../types';
import { getSampleDisplayImage } from '../Home/SampleImages/sampleImages';

/**
 * Parse the current fullscreen image ID from the URL. This handles the following cases:
 * - Fullscreen generated image from the results page
 * - Fullscreen original image from the results page
 * - Fullscreen generated image from the favorites page
 *
 * @returns object containing the parsed `fullscreenImageId` which
 * can be either a generated or original image or a parse failure
 */
const useActiveFullscreenImageParams = () => {
  // Get params from useRouteMatch as this reliably returns params on first
  // render unlike useParams which requires the route be fully mounted. This
  // fixes an issue where params are empty on first render in this hook
  const { params } = useRouteMatch(fullscreenImageRoutes) ?? { params: {} };

  const displayParams = parseParams(
    params,
    object({
      aiDisplayImageId: union([number().required(), string().required()]),
    }),
  );

  const isFullscreen = useIsFullscreenImageOpen();

  if (!isFullscreen) return {};

  return '__error' in displayParams
    ? displayParams
    : ({
        fullscreenImageId: displayParams.aiDisplayImageId,
        type: 'display',
      } as const);
};

export const useActiveFullscreenImageId = () =>
  useActiveFullscreenImageParams().fullscreenImageId;

/**
 * Optionally pass images to avoid making authenticated network requests to get
 * original images and other image pairs.
 */
interface UseActiveFullscreenImageProps {
  images?: AiOrSampleDisplayImage[];
}

/**
 * Queries for the generated image with the ID specified in the URL as the
 * current fullscreen image (i.e: `/saved/:aiGeneratedImage`). If the image
 * ID cannot be parsed from the URL, `image` will be `undefined` and the parsing
 * error will be available as `error`
 *
 * @todo we should throw the params parsing error from `__error` if there's a
 * parse failure while on one of the `fullscreenRoutes` and surface the error to
 * the user with an error boundary
 */
export const useActiveFullscreenImage = ({
  images,
}: UseActiveFullscreenImageProps) => {
  const params = useActiveFullscreenImageParams();
  const { fullscreenImageId, type } = params;
  const failed = '__error' in params;
  const isSample = typeof fullscreenImageId === 'string';

  const displayResult = useAiDisplayImageQuery({
    variables: {
      id: typeof fullscreenImageId === 'number' ? fullscreenImageId : NaN,
    },
    skip: !!images || failed || type !== 'display' || isSample,
  });

  const providedImage = images?.find(
    image => getImageId(image) === fullscreenImageId,
  );

  if (providedImage) return { image: providedImage, loading: false };

  if (failed) {
    const { __error: paramsError } = params;

    return {
      image: null,
      loading: false,
      error: paramsError,
    };
  }

  if (isSample) {
    return {
      image: getSampleDisplayImage(fullscreenImageId),
      loading: false,
    };
  }

  const { data, loading } = displayResult;

  return {
    image: data?.aiDisplayImage,
    loading,
  };
};
